// totp.service.ts
import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { HttpHeaders } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class TotpService {
  private cachedSecret = '';
  private step = 30;

  constructor() { // testando
  }
  

  async setSecret(secret: string): Promise<void> {
    console.log('[TOTP] Salvando secret no SecureStorage...');
    try {
      await SecureStoragePlugin.set({ key: 'app_totp_secret', value: secret });
      this.cachedSecret = secret;
    } catch (err) {
      console.error('[TOTP] Erro ao salvar secret:', err);
      throw err;
    }
  }


  saveJwtTokens(context: 'user' | 'creator', accessToken: string, totpSecret?: string) {
    if (context === 'user') {
      localStorage.setItem('access_token_user', accessToken);
      if (totpSecret) SecureStoragePlugin.set({ key: 'app_totp_secret_user', value: totpSecret });
    } else {
      localStorage.setItem('access_token_creator', accessToken);
      if (totpSecret) SecureStoragePlugin.set({ key: 'app_totp_secret_creator', value: totpSecret });
    }
  }
  

  private async getSecret(): Promise<string> {
    console.log('[TOTP] Buscando secret...');
    if (this.cachedSecret) {
      console.log('[TOTP] Secret já em cache');
      return this.cachedSecret;
    }

    if (!Capacitor.isNativePlatform()) {
      console.warn('[TOTP] Tentativa de acesso ao secret fora do ambiente nativo');
      throw new Error('TOTP só pode ser acessado em ambiente nativo.');
    }

    try {
      const { value } = await SecureStoragePlugin.get({ key: 'app_totp_secret' });
      console.log('[TOTP] Secret recuperado do SecureStorage');
      this.cachedSecret = value;
      return value;
    } catch (err) {
      console.error('[TOTP] Erro ao recuperar secret:', err);
      throw err;
    }
  }

  async getCurrentCode(): Promise<string> {
    //console.log('[TOTP] Gerando código atual...');

    // 🚫 Se não for ambiente nativo, não tenta gerar TOTP
    if (!Capacitor.isNativePlatform()) {
      //console.warn('[TOTP] Execução ignorada — ambiente não é nativo.');
      return ''; // ou null se preferir
    }

    try {
      const secret = await this.getSecret();
      //console.log('[TOTP] Secret obtido:', secret);

      const decodedKey = this.base32Decode(secret);
      const time = Math.floor(Date.now() / 1000 / this.step);
      const timeBytes = this.intToBytes(time);

      // 🔒 Gerar HMAC SHA-1 usando Web Crypto
      const key = await crypto.subtle.importKey(
        'raw',
        decodedKey,
        { name: 'HMAC', hash: { name: 'SHA-1' } },
        false,
        ['sign']
      );

      const signature = await crypto.subtle.sign('HMAC', key, timeBytes);
      const hmacBytes = new Uint8Array(signature);
      const hmacHex = Array.from(hmacBytes).map(b => b.toString(16).padStart(2, '0')).join('');
      console.log('[TOTP] HMAC SHA-1 (hex):', hmacHex);

      const code = this.dynamicTruncate(hmacHex);
      const finalCode = this.pad(code % 1_000_000);
      return finalCode;
    } catch (err) {
      console.error('[TOTP] Erro ao gerar código TOTP:', err);
      throw err;
    }
  }


  private pad(num: number): string {
    const padded = num.toString().padStart(6, '0');
    console.log('[TOTP] Código com padding:', padded);
    return padded;
  }

  private intToBytes(num: number): Uint8Array {
    const bytes = new Uint8Array(8);
    for (let i = 7; i >= 0; i--) {
      bytes[i] = num & 0xff;
      num >>= 8;
    }
    console.log('[TOTP] intToBytes:', bytes);
    return bytes;
  }

  private dynamicTruncate(hmacHex: string): number {
    const offset = parseInt(hmacHex.slice(-1), 16);
    const bin = hmacHex.slice(offset * 2, offset * 2 + 8);
    const result = parseInt(bin, 16) & 0x7fffffff;
    //console.log('[TOTP] dynamicTruncate → offset:', offset, 'bin:', bin, 'result:', result);
    return result;
  }

  private base32Decode(input: string): Uint8Array {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    const clean = input.replace(/=+$/, '').toUpperCase();
    let bits = '';
    for (const char of clean) {
      const val = alphabet.indexOf(char);
      if (val === -1) {
        console.error('[TOTP] base32Decode → caractere inválido:', char);
        throw new Error('Caractere inválido no segredo Base32.');
      }
      bits += val.toString(2).padStart(5, '0');
    }

    const bytes = new Uint8Array(Math.floor(bits.length / 8));
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(bits.slice(i * 8, i * 8 + 8), 2);
    }

    console.log('[TOTP] base32Decode → resultado final:', bytes);
    return bytes;
  }

  async getChatHttpOptions() {
    // 🔹 Gera o TOTP como já estava
    const token = await this.getCurrentCode();

    // 🔹 Lê o user_id do localStorage
    const userId = localStorage.getItem('user_id') || '';

    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-token': token,
        'x-user-id': userId
      })
    };
  }


}

